import { Icon } from "@blueprintjs/core";
import { fabric } from "fabric";
import React, { useEffect, useRef } from "react";

export function UploadComponet({
  Canvaseditor,
  setShowToolbar,
  setelementStrokColor,
  setTextToolbar,
  setshowUploadToolbar,
  closeTab,
}) {
  const uploadedImages = useRef([]);
  const canvasRef = useRef(null);

  useEffect(() => {
    if (Canvaseditor) {
      canvasRef.current = Canvaseditor;
      // console.log(Canvaseditor, "canvasObject");
    }
  }, [Canvaseditor]);

  // ========== DRAG-AND-DROP IMAGE UPLOAD (Added Feature) ==========
  // Users can upload images by dragging files from their computer/folder and dropping them
  // onto this Upload panel. Same logic as the existing "Upload Image" button - no duplicate code.

  const ACCEPTED_IMAGE_TYPES = ["image/jpeg", "image/png", "image/gif", "image/webp", "image/svg+xml", "image/bmp"];
  const IMAGE_EXTENSIONS = /\.(jpe?g|png|gif|webp|svg|bmp)$/i;

  /** Validates if a file is an image. Uses MIME type first, falls back to file extension (for drag-drop from some file managers where type may be empty). */
  const isImageFile = (file) => {
    if (!file || !(file instanceof File)) return false;
    if (file.type && (file.type.startsWith("image/") || ACCEPTED_IMAGE_TYPES.includes(file.type))) return true;
    if (file.name && IMAGE_EXTENSIONS.test(file.name)) return true;
    return false;
  };

  /** Extracts files from drag-drop event. Uses both dataTransfer.files and dataTransfer.items (some browsers/sources only provide one). */
  const getFilesFromDataTransfer = (dataTransfer) => {
    if (!dataTransfer) return [];
    const files = [];
    if (dataTransfer.files && dataTransfer.files.length > 0) {
      files.push(...Array.from(dataTransfer.files));
    }
    // Fallback: when dragging from another tab/app, files might be empty - try items.getAsFile()
    if (files.length === 0 && dataTransfer.items && dataTransfer.items.length > 0) {
      for (let i = 0; i < dataTransfer.items.length; i++) {
        const item = dataTransfer.items[i];
        if (item.kind === "file") {
          const file = item.getAsFile();
          if (file) files.push(file);
        }
      }
    }
    return files;
  };

  /** Shared logic for both click-to-upload and drag-drop. Validates images, reads with FileReader, adds to list. Refactored from original uploadImage to avoid code duplication. */
  const processFiles = (fileList) => {
    if (!fileList || fileList.length === 0) return;
    const files = Array.from(fileList).filter(isImageFile);
    if (files.length === 0) return;

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const reader = new FileReader();

      reader.onload = function (e) {
        const img = new Image();
        img.onload = function () {
          uploadedImages.current.push({
            src: e.target.result,
            width: img.width,
            height: img.height,
          });
          updateImageList();
        };
        img.src = e.target.result;
      };

      reader.readAsDataURL(file);
    }

    const resizeWidth = window.innerWidth;
    if (resizeWidth <= 770) {
      closeTab();
    }
  };

  /** File input onChange handler - now delegates to processFiles (same logic as drag-drop). */
  const uploadImage = (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    processFiles(files);
  };

  // --- Drag-and-drop event handlers (required for drop to work) ---
  /** Must call preventDefault on dragover - otherwise browser won't fire drop event. */
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer) {
      e.dataTransfer.dropEffect = "copy";
    }
  };

  /** Sets effectAllowed so browser accepts the drop (fixes some browsers where drop was ignored). */
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer) {
      e.dataTransfer.effectAllowed = "copy";
    }
  };

  /** Prevents browser default (e.g. opening file in new tab) when drag leaves the drop zone. */
  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  /** When files are dropped: extract files (using both files and items API), ignore empty drops, then process same as button upload. */
  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const files = getFilesFromDataTransfer(e.dataTransfer);
    if (files.length === 0) return;
    processFiles(files);
  };

  const updateImageList = () => {
    const imageList = document.getElementById("imageList");
    if (!imageList) return;
    imageList.innerHTML = "";
    uploadedImages.current.forEach((item, index) => {
      const imgElement = document.createElement("img");
      imgElement.src = item.src;
      imgElement.loading = "lazy";
      imgElement.style.height = "auto";
      imgElement.addEventListener("click", function () {
        bindImageToCanvas(index);
        displayImageDetails(index);
      });
      imageList.appendChild(imgElement);
    });
  };

  const bindImageToCanvas = (index) => {
    const selectedImage = uploadedImages.current[index];
    const canvas = canvasRef.current;
    const canvasWidth = canvas.getWidth();
    const canvasHeight = canvas.getHeight();
    const imageWidth = selectedImage.width;
    const imageHeight = selectedImage.height;

    const imgElement = new Image();
    imgElement.onload = () => {
      const fabricImage = new fabric.Image(imgElement, {
        left: (canvasWidth - imageWidth) / 2,
        top: (canvasHeight - imageHeight) / 2,
        scaleX: 0.5,
        scaleY: 0.5,
      });

      const scaleX = canvasWidth / imageWidth;
      const scaleY = canvasHeight / imageHeight;
      const scale = Math.min(scaleX, scaleY);

      fabricImage.set({
        scaleX: scale,
        scaleY: scale,
        left: (canvasWidth - imageWidth * scale) / 2,
        top: (canvasHeight - imageHeight * scale) / 2,
      });

      canvas.add(fabricImage).setActiveObject(fabricImage);

      fabricImage.on("scaling", function () {
        displayImageDetails(index);
      });

      fabricImage.on("selected", function () {
        displayImageDetails(index);
      });

      fabricImage.on("moving", function () {
        displayImageDetails(index);
      });

      fabricImage.on("rotating", function () {
        displayImageDetails(index);
      });

      canvas.centerObject(fabricImage);
      canvas.renderAll();
      if (typeof canvas.fire === "function") canvas.fire("canvas:state-changed");
      setShowToolbar(true);
      setshowUploadToolbar(true);
      setelementStrokColor(false);
      setTextToolbar(false);
    };

    imgElement.src = selectedImage.src;
  };

  const displayImageDetails = (index) => {
    const imageDetails = document.getElementById("imageDetails");
    if (!imageDetails) return; // Check if the element exists

    imageDetails.innerHTML = "";
    const canvas = canvasRef.current;
    const selectedImage = uploadedImages.current[index];
    const activeObject = canvas.getActiveObject();

    // Check if the activeObject is defined and is an instance of fabric.Image
    if (activeObject && activeObject instanceof fabric.Image) {
      const inchWidth = activeObject.getScaledWidth() / canvas.getZoom() / 96;
      const inchHeight = activeObject.getScaledHeight() / canvas.getZoom() / 96;
      const dpiWidth = selectedImage.width / inchWidth;
      const detailsDiv = document.createElement("div");
      const inchLeft = activeObject.left / canvas.getZoom() / 96;
      const inchTop = activeObject.top / canvas.getZoom() / 96;
      detailsDiv.innerHTML = `
        <p>Left: ${inchLeft.toFixed(2)} inches</p>
        <p>Top:  ${inchTop.toFixed(2)} inches</p>
        <p>Width: ${inchWidth.toFixed(2)} inches</p>
        <p>Height: ${inchHeight.toFixed(2)} inches</p>
        <p>Resolution : ${dpiWidth.toFixed(2)} px</p>
      `;
      imageDetails.appendChild(detailsDiv);
    }
  };

  // Drop zone: entire panel accepts drag-drop. minHeight 200 ensures enough area to reliably receive drops.
  return (
    <div
      style={{ height: "100%", minHeight: 200, display: "flex", flexDirection: "column" }}
      onDragOver={handleDragOver}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      <div style={{ color: "#5c7080", fontSize: "14px", padding: "8px 0 6px" }}>
        Do you want to upload your own images?
      </div>

      <div
        onClick={() => document.getElementById("input-file")?.click()}
        style={{
          border: "2px dashed #c5d0da",
          borderRadius: "8px",
          backgroundColor: "#f5f8fa",
          padding: "24px 16px",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          cursor: "pointer",
          marginBottom: "16px",
          transition: "border-color 0.2s, background-color 0.2s",
        }}
        onMouseEnter={(e) => { e.currentTarget.style.borderColor = "#5c7080"; e.currentTarget.style.backgroundColor = "#ebf1f5"; }}
        onMouseLeave={(e) => { e.currentTarget.style.borderColor = "#c5d0da"; e.currentTarget.style.backgroundColor = "#f5f8fa"; }}
      >
        <Icon size={28} icon="cloud-upload" style={{ color: "#394b59", marginBottom: "8px" }} />
        <div style={{ fontWeight: 600, fontSize: "14px", color: "#1c2127", marginBottom: "4px" }}>
          Drag and drop images here
        </div>
        <div style={{ fontSize: "12px", color: "#8a9ba8", marginBottom: "12px" }}>
          or click to browse from your device
        </div>
        <label
          htmlFor="input-file"
          className="bp4-button"
          style={{ pointerEvents: "none" }}
        >
          <Icon size={16} icon="upload" />
          <span className="bp4-button-text">Upload Images</span>
        </label>
        <input
          onChange={uploadImage}
          type="file"
          id="input-file"
          accept="image/*"
          style={{ display: "none" }}
          multiple
        />
      </div>

      <div id="imageDetails"></div>
      <div id="imageList"></div>
    </div>
  );
}
